import type {
  FluencyDeckReport,
  GetFluencyDeckReportQuery,
} from '@innovamat/glow-api-client';
import {
  GetFluencyDeckReportDocument,
  useGlowApiClient,
} from '@innovamat/glow-api-client';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { useFluencyCourseDecks } from '../../../hooks/use-fluency-course-decks';
import type { DeckReport } from '../../../modules/fluency/deck-report';
import { sortDeckReports } from '../../../modules/fluency/deck-report';

export function useFluencyDeckQueries(
  allDecks: ReturnType<typeof useFluencyCourseDecks>['allDecks'],
  classroomId: string,
  hasDecks: boolean
): { isLoading: boolean; fluencyDeckReports: DeckReport[] } {
  const { graphqlClient } = useGlowApiClient();

  const queriesConfig = useMemo(() => {
    return allDecks.map((deck) => ({
      queryKey: ['fluencyDeckReport', classroomId, deck.courseId, deck.deckId],
      queryFn: async () => {
        const result = await graphqlClient.request<GetFluencyDeckReportQuery>(
          GetFluencyDeckReportDocument,
          {
            classroomId,
            courseId: deck.courseId,
            deckId: deck.deckId,
          }
        );
        return result.fluencyDeckReport;
      },
      enabled: hasDecks,
    }));
  }, [allDecks, classroomId, hasDecks, graphqlClient]);

  const queries = useQueries({
    queries: queriesConfig,
  });

  const isLoading = queries.some((query) => query.isLoading);
  const fluencyDeckReports = sortDeckReports(
    queries
      .map((query) => query.data)
      .filter((report): report is FluencyDeckReport => Boolean(report))
  );

  return { isLoading, fluencyDeckReports };
}
